import { Injectable } from '@angular/core';
import { PunchoutError, PunchoutOrderRow } from '../../../models/punchout';
import { OciPunchoutHandler } from './oci-punchout.handler';

/**
 * OCI mapper for Visma Proceedo, covering every parameter Proceedo reads
 * See https://vismacose.my.salesforce.com/sfc/p/#5J000000o8Hj/a/P9000003mRFV/7kmf_xf_CuRT6FjeNkCyVlI7JIP0Bl.Vmr3KkDXhjk4
 * 
 * PIM fields that are represented in this Proceedo mapper are currently generic, and may or may not exist 
 * depending on the customer. The intention is to have a generic Proceedo OCI mapper that "works" for all customers, 
 * and to have the PIM fields be customer-specific.
 *
 * - NEW_ITEM-VENDORMAT     vendor article number (EXT_PRODUCT_ID is not read by Proceedo)
 * - NEW_ITEM-DESCRIPTION   item description, max 40 characters
 * - NEW_ITEM-PRICE         net unit price, max 3 decimals, dot separator, no thousand separators
 * - NEW_ITEM-PRICEUNIT     price unit, whole number (inherited from the base mapper, default 1)
 * - NEW_ITEM-LEADTIME      delivery time in whole days, sent when the product carries one
 * - NEW_ITEM-CURRENCY      ISO currency code (inherited from the base mapper)
 * - NEW_ITEM-UNIT          ISO unit of measure, max 3 characters
 * - NEW_ITEM-QUANTITY      ordered quantity, whole number
 * - NEW_ITEM-EXT_CATEGORY  UNSPSC code, sent directly without EXT_SCHEMA_TYPE when available
 * - NEW_ITEM-CUST_FIELD2   row VAT rate
 */
@Injectable({ providedIn: 'root' })
export class ProceedoOciPunchoutHandler extends OciPunchoutHandler {
  override readonly protocol = 'oci';
  override readonly identifier = 'proceedo-default';
  override readonly label = 'Proceedo (default)';

  protected override productIdField = 'VENDORMAT';
  /** Standard Swedish VAT rate, used unless the product carries an explicit rate. */
  protected defaultVatRate = '25';

  /**
   * Builds the row fields from scratch — deliberately NOT a union with the generic OCI mapper's
   * output. The payload contains exactly the parameters Proceedo reads, nothing else.
   */
  protected override mapRow(
    row: PunchoutOrderRow,
    index: number,
    extras: { punchoutOrderId?: string }
  ): { [s: string]: string } {
    const i = index + this.indexBase;
    const quantity = row.quantity ?? 1;
    if (!Number.isFinite(quantity)) {
      throw new PunchoutError('PUNCHOUT_OCI_INVALID_QUANTITY', 'OCI punchout quantity is invalid.');
    }
    if (!Number.isFinite(row.price)) {
      throw new PunchoutError('PUNCHOUT_OCI_INVALID_PRICE', 'OCI punchout price is invalid.');
    }

    const fields: { [s: string]: string } = {
      [`NEW_ITEM-${this.productIdField}[${i}]`]: this.getRowId(row),
      [`NEW_ITEM-DESCRIPTION[${i}]`]: (row.product?.name ?? '').substring(0, 40),
      [`NEW_ITEM-PRICE[${i}]`]: this.formatPrice(row.price),
      [`NEW_ITEM-PRICEUNIT[${i}]`]: this.priceUnit,
      [`NEW_ITEM-CURRENCY[${i}]`]: row.currency ?? '',
      [`NEW_ITEM-UNIT[${i}]`]: this.resolveUnit(row),
      [`NEW_ITEM-QUANTITY[${i}]`]: String(Math.round(quantity)),
      [`NEW_ITEM-CUST_FIELD2[${i}]`]: this.resolveVatRate(row)
    };
    const leadTime = this.resolveLeadTime(row);
    if (leadTime) {
      fields[`NEW_ITEM-LEADTIME[${i}]`] = leadTime;
    }
    const unspsc = this.resolveUnspsc(row);
    if (unspsc) {
      fields[`NEW_ITEM-EXT_CATEGORY[${i}]`] = unspsc;
    }
    return fields;
  }

  /** Max 3 decimals, dot as decimal separator, no thousand separators. */
  protected formatPrice(price: number): string {
    const formatted = String(parseFloat(price.toFixed(3)));
    const digitsBeforeDecimal = formatted.replace('-', '').split('.')[0].length;
    if (digitsBeforeDecimal > 11) {
      throw new PunchoutError('PUNCHOUT_OCI_INVALID_PRICE', 'OCI punchout price exceeds 11 digits before the decimal point.');
    }
    return formatted;
  }

  /** ISO unit of measure, max 3 characters. Falls back to the standard mapper's static unit. */
  protected resolveUnit(row: PunchoutOrderRow): string {
    const unit = row.product?.extra?.pim?.unit;
    return typeof unit === 'string' && unit ? unit.substring(0, 3) : this.unit;
  }

  /** Delivery lead time in whole days; omitted when the product does not carry one. */
  protected resolveLeadTime(row: PunchoutOrderRow): string {
    const leadTime = row.product?.extra?.pim?.leadTime;
    if (leadTime === undefined || leadTime === null || leadTime === '') {
      return '';
    }
    const days = Math.round(Number(leadTime));
    return Number.isFinite(days) ? String(days) : '';
  }

  protected resolveVatRate(row: PunchoutOrderRow): string {
    const vat = row.product?.extra?.pim?.vatRate;
    return typeof vat === 'string' && vat ? vat : this.defaultVatRate;
  }

  protected resolveUnspsc(row: PunchoutOrderRow): string {
    return (row.product?.extra?.pim?.unspsc as string) || '';
  }
}
